import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { Link } from 'react-router-dom';
import EventNoteRoundedIcon from '@mui/icons-material/EventNoteRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import {
  formatMeasureDisplayName,
  getMeasureJurisdictionLabel,
  formatElectionDate,
  formatBallotStatus,
  stripHtml,
  buildBallotMeasurePath,
} from '../../utils/ballotMeasuresApi';
import { shape } from '../../theme/shape';
import { theme } from '../../theme/colors';

const SUMMARY_PREVIEW_CHARS = 200;

export default function BallotMeasureCard({ measure, listSearch = '' }) {
  if (!measure) return null;

  const title = formatMeasureDisplayName(measure);
  const jurisdiction = getMeasureJurisdictionLabel(measure);
  const electionDate = formatElectionDate(measure.electionDate);
  const summaryRaw = stripHtml(measure.summary || measure.ballotQuestion || '');
  const summaryPreview =
    summaryRaw.length > SUMMARY_PREVIEW_CHARS
      ? `${summaryRaw.slice(0, SUMMARY_PREVIEW_CHARS).trim()}…`
      : summaryRaw;
  const detailPath = buildBallotMeasurePath(measure);
  const backSearch = listSearch ? `?${listSearch.replace(/^\?/, '')}` : '';

  const statusLabel = formatBallotStatus(measure);

  return (
    <Box
      component={Link}
      to={{ pathname: detailPath, search: backSearch ? backSearch : undefined }}
      state={{ measure }}
      className="bill-card ballot-measure-card"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        p: 2,
        bgcolor: '#fff',
        borderRadius: shape.radius.card,
        border: '1px solid #e2e8f0',
        textDecoration: 'none',
        color: 'inherit',
        transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
        '&:hover': {
          borderColor: '#94a3b8',
          boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Typography
          component="h2"
          sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.35, m: 0 }}
        >
          {title}
        </Typography>
        <Chip
          label="Ballot measure"
          size="small"
          sx={{
            flexShrink: 0,
            fontWeight: 600,
            fontSize: '0.7rem',
            bgcolor: theme.primary,
            color: '#fff',
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
        {jurisdiction && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PlaceRoundedIcon sx={{ fontSize: 16, color: '#475569' }} />
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>
              {jurisdiction}
            </Typography>
          </Box>
        )}
        {electionDate && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <EventNoteRoundedIcon sx={{ fontSize: 16, color: '#475569' }} />
            <Typography sx={{ fontSize: '0.85rem', color: '#334155' }}>
              Election: {electionDate}
            </Typography>
          </Box>
        )}
      </Box>

      {summaryPreview && (
        <Typography sx={{ fontSize: '0.9rem', color: '#334155', lineHeight: 1.55, m: 0 }}>
          {summaryPreview}
        </Typography>
      )}

      {statusLabel && (
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#475569', m: 0 }}>
          Ballot status: {statusLabel}
        </Typography>
      )}
    </Box>
  );
}
